import type { Event } from "@bugsnag/js";

import useSystemStore from "@store/useSystemStore";
import { EMAIL_QUERY_KEY, MEETING_QUERY_KEY, THREAD_QUERY_KEY } from "@config/query";
import { ERROR_STRINGS } from "@config/messages";

/**
 * Events Bugsnag should drop before sending. Matched in App.tsx's onError
 * gate. Predicates read three fields populated by useBugsnagErrorHandler:
 * errors[0].errorMessage, getMetadata("react-query").callSite, and
 * getMetadata("http").status.
 *
 * Add entries below; the `name` field is documentation only.
 */

type BugsnagNoiseFilter = {
  name: string;
  matches: (event: Event) => boolean;
};

const errorMessage = (event: Event): string => event.errors?.[0]?.errorMessage ?? "";

const callSite = (event: Event): string => {
  const raw = event.getMetadata("react-query") as { callSite?: unknown } | undefined;
  return typeof raw?.callSite === "string" ? raw.callSite : "";
};

const httpStatus = (event: Event): number | undefined => {
  const raw = event.getMetadata("http") as { status?: unknown } | undefined;
  return typeof raw?.status === "number" ? raw.status : undefined;
};

export const isIgnorableBugsnagEvent = (event: Event): boolean =>
  BUGSNAG_NOISE_FILTERS.some((filter) => filter.matches(event));

const BUGSNAG_NOISE_FILTERS: BugsnagNoiseFilter[] = [
  {
    // Every request in flight when a window opens fails this way; outside maintenance
    // mode a 503 is a real incident and must keep reporting.
    name: "503 while under maintenance",
    matches: (event) => httpStatus(event) === 503 && useSystemStore.getState().isMaintenance,
  },
  {
    name: "OAuth re-auth UI: user sees a [couldn't log you in] toast",
    matches: (event) => errorMessage(event) === "Authorization page could not be loaded.",
  },
  {
    name: "OAuth: user declined access",
    matches: (event) => errorMessage(event) === "The user did not approve access.",
  },
  {
    name: "get-email 400 (email not synced)",
    matches: (event) => callSite(event).startsWith(EMAIL_QUERY_KEY) && httpStatus(event) === 400,
  },
  {
    name: "get-thread / get-meeting 404 (entity deleted server-side)",
    matches: (event) => {
      const cs = callSite(event);
      return (
        (cs.startsWith(THREAD_QUERY_KEY) || cs.startsWith(MEETING_QUERY_KEY)) &&
        httpStatus(event) === 404
      );
    },
  },
  {
    name: "create-company validation failure (e.g. website duplicate)",
    matches: (event) =>
      callSite(event) === ERROR_STRINGS.CREATE_COMPANY && httpStatus(event) === 400,
  },
];
